// TODO: --> Check proper authentication and authorization of the user before routing using product Id

import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

// utils
import { STATUS_STYLES, INVENTORY_STYLES } from "@/utils/statusStyles";
import { searchFilterProducts } from "@/views/admin/products/utils/searchFilterProducts";

// reusable components
import TableActionsMenu from "@/components/ui/TableActionsMenu";
import { TableRow, TableCell } from "@/components/ui/table";
import ProductImageCell from "@/components/ProductImageCell";
import { fetchProducts } from "@/services/catalogueApi";
import { useBusiness } from "@hooks/useBusiness";
import { useStorePath } from "@hooks/useStorePath";

export default function useProductsTable() {
  const { businessId } = useBusiness();

  // A reusable validation function
  function validateProductsData(data) {
    if (!Array.isArray(data)) return [];

    return data.filter((product, index) => {
      const issues = [];
      
      if (typeof product.productId !== "string") issues.push("productId");
      if (typeof product.productName !== "string") issues.push("productName");
      if (typeof product.productStatus !== "string") issues.push("productStatus");
      if (typeof product.productCategory !== "string") issues.push("productCategory");
      if (typeof product.inventoryQuantity !== "number") issues.push("inventoryQuantity");
      if (typeof product.productPrice !== "number" && typeof product.productRetailPrice !== "number") issues.push("productPrice");
      if (!Array.isArray(product.productImages)) issues.push("productImages");

      if (issues.length > 0) {
        console.warn(`Product at index ${index} failed validation:`, issues, product);
        return false;
      }

      return true;
    });
  }

    // TODO: Persist react query cache to IndexDB
    // TODO: Show updating when background refetch is happening
    // TODO: use mutation to handle status updates in the backend
    // TODO: add a refresh button for manual refreshing..
    // TODO: Implement paginated fetching from the backend
    // fetching and caching the data from the mock json file
  const {
      data: products = [],
      isLoading,
      isError,
      refetch,
  } = useQuery({
      queryKey: ["products", businessId],
      queryFn: async () => validateProductsData(await fetchProducts(businessId)),
      enabled: Boolean(businessId),
      staleTime: 1000 * 60 * 5, // cache for 5 minutes
  });

  // implement persisitence of search and filters
  // capture states for the search and filter functionality
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "All";

  const navigate = useNavigate();
  const { storePath } = useStorePath();

  const updateSearchTerm = useCallback((val) => {
    const newParams = new URLSearchParams(searchParams);
    if (val) {
      newParams.set("search", val);
    } else {
      newParams.delete("search");
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const updateStatusFilter = useCallback((val) => {
    const newParams = new URLSearchParams(searchParams);
    if (val && val !== "All") {
      newParams.set("status", val);
    } else {
      newParams.delete("status");
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // TODO: Replace with the backend call that accepts status and search as params
  // Memoize the filtered data
  const filteredProducts = useMemo(() => {
    return searchFilterProducts(products, searchTerm, statusFilter);
  }, [products, searchTerm, statusFilter]);

  // route to view/edit product page
  const handleView = (product) => {
    if (!product?.productId) {
      console.error("Missing product ID");
      return;
    }
    navigate(storePath(`products/${product.productId}`));
  };

  const handleChangeStatus = (productId, newStatus) => {
    // TODO: Connect to backend to update the status of the product
    // TODO: Add proper fallbacks; loading, confirmation, error, toast
  };

  // memoize the productColumns to prevent unecessary table re-renders
  const productColumns = useMemo(() => [
    // Initialize the definitions of the table columns
    // for the select column
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          className="rounded"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="rounded"
          checked={row.getIsSelected()}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => row.toggleSelected(!!e.target.checked)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // product image column
    {
      id: "image",
      header: "Product Image",
      cell: ({ row }) => {
        const imageUrl = row.original?.productImages?.[0]?.url;
        const alt = row.original?.productImages?.[0]?.alt ?? row.original?.productName ?? "Product Image";

        return <ProductImageCell imageUrl={imageUrl} alt={alt} />;
      },
      enableSorting: false,
    },

    // product name column
    {
      accessorKey: "productName",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Product Name
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => {
        const name = row.original?.productName;
        const displayName = typeof name === "string" && name.trim() !== "" ? name : "Unnamed Product";
        const isFallback = displayName === "Unnamed Product";

        return (
          <span className={isFallback ? "text-gray-400 italic" : ""}>
            {displayName}
          </span>
        );
      }
    },

    // status column
    {
      accessorKey: "productStatus",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => {
        const status = row.original?.productStatus;

        const validStatus = typeof status === "string" && status.trim().length > 0
          ? status.toLowerCase()
          : "unknown";

        const style = STATUS_STYLES[validStatus] ?? {
          label: "Unknown",
          color: "bg-gray-100 text-gray-800 font-semibold italic",
        };

        return (
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${style.color}`}>
            {style.label}
          </span>
        );
      },
    },

    // inventory column
    {
      accessorKey: "inventoryQuantity",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Inventory
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => {
        const rawQty = row.original?.inventoryQuantity;
        const isValidNumber = typeof rawQty === "number" && rawQty >= 0;
        const qty = isValidNumber ? rawQty : 0;

        const inventoryStyle =
          qty === 0
            ? INVENTORY_STYLES.none
            : qty <= 2
            ? INVENTORY_STYLES.low
            : INVENTORY_STYLES.normal;

        const label = isValidNumber ? `${qty} in stock` : "Not Available";
        
        return <span className={`${inventoryStyle.color}`}>{label}</span>;
      },
    },

    // price column
    {
      accessorKey: "productPrice",
      header: ({ column }) => (
        <button
          className="flex items-center gap-1"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Retail Price (KES)
          {column.getIsSorted() === "asc" && <span>↑</span>}
          {column.getIsSorted() === "desc" && <span>↓</span>}
        </button>
      ),
      cell: ({ row }) => {
        const rawPrice = typeof row.original?.productPrice === "number" ? row.original.productPrice : row.original?.productRetailPrice;
        const isValidPrice = typeof rawPrice === "number" && isFinite(rawPrice) && rawPrice >= 0;

        return isValidPrice
          ? `KES ${rawPrice.toLocaleString()}`
          : <span className="text-gray-400 italic">Price Unavailable</span>;
      }
    },

    // category column
    {
      accessorKey: "productCategory",
      header: "Product Category",
      cell: ({ row }) => {
        const category = row.original?.productCategory;
        const isValid = typeof category === "string" && category.trim() !== "";

        return (
          <span className={isValid ? "" : "text-gray-400 italic"}>
            {isValid ? category : "-"}
          </span>
        );
      }
    },

    // modal menu for more information
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const product = row.original;
        const actions = [];

        // if product is active, offer to archive it
        if (product.productStatus === "active") {
          actions.push({
            label: "Archive Product",
            onClick: () => handleChangeStatus(product.productId, "archived"),
            className: "text-red-500",
          });
        }

        // if product is archived, offer to activate it
        if (product.productStatus === "archived" || product.productStatus === "draft") {
          actions.push({
            label: "Activate Product",
            onClick: () => handleChangeStatus(product.productId, "active"),
            className: "text-green-500",
          });
        }

        // always included
        actions.unshift({
          label: "View Product",
          onClick: () => handleView(product),
        });

        return <TableActionsMenu actions={actions} />;
      },
      enableSorting: false,
      enableHiding: false,
    },
  ], []);

// render each row of the table using the data + column config
// memoize to stabilise the function and reduse re-renders or memo mismatch
  const renderProductRow = useCallback((row) => {
    const product = row.original;

    return (
      <TableRow key={row.id} onClick={() => handleView(product)} className="cursor-pointer hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell className="px-4 py-3 text-[13px] font-medium" key={cell.id}>
            {cell.getContext().column.columnDef.cell
              ? cell.getContext().column.columnDef.cell(cell.getContext())
              : cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
    
  }, [handleView]);

  return {
    allProducts: products,
    products: filteredProducts,
    searchTerm,
    updateSearchTerm,
    statusFilter,
    updateStatusFilter,
    refetch,
    productColumns,
    renderProductRow,
    isLoading,
    isError,
  };
}